import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import {
	type ConcreteRequest,
	type RecordSourceProxy,
	type RecordProxy,
	type ReaderSelection,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderArgument,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';

import { toDocumentNode } from './toDocumentNode.ts';

export type WriteQueryInteropArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<DataProxy.WriteQueryOptions<TData, TVariables>, 'data' | 'variables'> & {
	query: GraphQLTaggedNode;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function writeQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): void {
	return isRelayEnabled(params.__callerPackage__)
		? writeQueryInteropRelay<TData, TVariables>(params)
		: writeQueryInteropApollo<TData, TVariables>(params);
}

function writeQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: WriteQueryInteropArgs<TData, TVariables>,
): void {
	const relayEnv = window.GLOBAL_RELAY_ENV!;
	const { query, data, variables } = params;

	if (!data) {
		return;
	}

	relayEnv.commitUpdate((storeProxy: RecordSourceProxy) => {
		const root = storeProxy.getRoot();
		if (!root) {
			return;
		}

		const queryNode = query as ConcreteRequest;
		const queryFields = queryNode?.operation?.selections;

		if (queryFields) {
			writeQueryFields(
				storeProxy,
				root,
				queryFields as readonly ReaderSelection[],
				data,
				variables || {},
			);
		}
	});
}

function writeQueryFields(
	storeProxy: RecordSourceProxy,
	record: RecordProxy,
	fields: readonly ReaderSelection[],
	data: any,
	variables: Record<string, any>,
): void {
	if (!record || !data) {
		return;
	}

	for (const field of fields) {
		if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const fieldData = data[linkedField.name];
			if (fieldData !== undefined) {
				if (Array.isArray(fieldData)) {
					const linkedRecords = fieldData
						.map((item: any) => {
							if (item && typeof item === 'object') {
								const fieldKey = getFieldKey(linkedField, variables);
								const existingRecord = record.getLinkedRecord(fieldKey);
								if (existingRecord && linkedField.selections) {
									writeQueryFields(
										storeProxy,
										existingRecord,
										linkedField.selections,
										item,
										variables,
									);
									return existingRecord;
								}
							}
							return null;
						})
						.filter((rec): rec is RecordProxy => rec !== null);

					if (linkedRecords.length > 0) {
						const fieldKey = getFieldKey(linkedField, variables);
						record.setLinkedRecords(linkedRecords, fieldKey);
					}
				} else if (fieldData && typeof fieldData === 'object') {
					const fieldKey = getFieldKey(linkedField, variables);
					const linkedRecord = record.getLinkedRecord(fieldKey);

					// If no linked record exists but we have data with an ID, we can try to get the existing record,
					// or create a new record based on __typename. However, this shouldn't be a common use-case across the codebase,
					// so we'll skip these code paths for now
					if (linkedRecord && linkedField.selections) {
						writeQueryFields(
							storeProxy,
							linkedRecord,
							linkedField.selections,
							fieldData,
							variables,
						);
					}
				}
			}
		} else if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			if (data[scalarField.name] !== undefined) {
				record.setValue(data[scalarField.name], scalarField.name);
			}
		}
	}
}

function getFieldKey(field: ReaderLinkedField, variables: Record<string, any>): string {
	if (!field.args || field.args.length === 0) {
		return field.name;
	}

	// Compute storage key for fields with arguments
	const args = field.args
		.map((arg: ReaderArgument) => {
			let value: string;
			if (arg.kind === 'Literal') {
				value = JSON.stringify(arg.value);
			} else if (arg.kind === 'Variable') {
				value = JSON.stringify(variables[arg.variableName]);
			} else {
				// Fallback for other argument types
				value = JSON.stringify((arg as any).value);
			}
			return `${arg.name}:${value}`;
		})
		.join(',');

	return `${field.name}(${args})`;
}

function writeQueryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): void {
	return window['GLOBAL_APOLLO_CLIENT']!.writeQuery({
		...params,
		query: toDocumentNode(params.query),
	});
}
